import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { operationType } from '../../../../../services/types';
import { Loader, Main, Styled } from './style';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { IoCheckmarkCircleOutline } from 'react-icons/io5';
import { ExpandedBlock } from '../../../expanded';
import CopyToClipboard from 'react-copy-to-clipboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClipboard, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { webURL } from '../../../../../services/API';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

type TypeformTriggerProps = NodeProps<operationType> & {
    setChildMinimize?: (fn: (id: string) => void) => void;
    setChildSettings?: (fn: (id: string) => void) => void;
};

export function TypeformTrigger({ id, data, setChildMinimize, setChildSettings }: TypeformTriggerProps) {
    const { setNodes } = useReactFlow();
    const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isCopy, setIsCopy] = React.useState<boolean>(false);
    const [link, setLink] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>('');
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('id');

    React.useEffect(() => {
        if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
        if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setChildMinimize, setChildSettings]);

    // Auto-generate webhook URL when expanded (like Slack trigger)
    React.useEffect(() => {
        if (link === '' && isExpanded) {
            setIsLoading(true);
            // Delay to allow project to be saved first
            setTimeout(() => {
                retrieveLink();
            }, 6000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded, data?.triggerCredentials?.webhookUrl]);

    // Retrieve existing link or generate new one
    const retrieveLink = async () => {
        if (data?.triggerCredentials?.webhookUrl) {
            setLink(data.triggerCredentials.webhookUrl);
            setIsLoading(false);
            setError('');
        } else {
            await generateLink();
        }
    };

    const generateLink = async () => {
        try {
            const response = await axios.post(`${webURL}/triggers/typeform/generate-link`, {
                nodeIndex: data.index,
                projectId
            });

            const { nodeId, nodeSecret, webhookUrl } = response.data;

            // Save to frontend state
            setNodes(prev =>
                prev.map(node =>
                    node.data.index === data.index
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                triggerCredentials: { nodeId, nodeSecret, webhookUrl }
                            }
                        }
                        : node
                )
            );

            setLink(webhookUrl);
            setIsLoading(false);

        } catch (err: any) {
            console.error('Error generating Typeform webhook URL:', err);
            const message = err.response?.data?.message || err.message || 'Failed to generate webhook URL';
            if (message.includes('Trigger node does not exist')) {
                setError('Please SAVE the project first (Ctrl+S), then reopen this panel.');
            } else {
                setError(message);
            }
            setIsLoading(false);
        }
    };

    return (<>
        <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
        <Main>
            {data.output ? <>
                <div id="ran"></div>
                <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </> : null}
            {isExpanded ? <ExpandedBlock
                data={{ id, ...data }}
                handleClose={() => setIsExpanded(false)}
                isWarning={false}
                style={{ minWidth: '400px' }}
            >
                {isLoading ? <Loader /> : <Styled>
                    {/* Webhook URL Section */}
                    <div className="webhook-section">
                        <h4>Typeform Webhook URL</h4>

                        {/* Show error message if any */}
                        {error && <div className="error-message" style={{ color: '#ff6b6b', marginBottom: '10px', fontSize: '13px' }}>{error}</div>}

                        {/* Show link directly (auto-generated) */}
                        {link && (
                            <CopyToClipboard
                                text={link}
                                onCopy={() => {
                                    setIsCopy(true);
                                    setTimeout(() => setIsCopy(false), 500);
                                }}
                            >
                                <div className="link-container" title={link}>
                                    <div className="link-with-copy">
                                        <FontAwesomeIcon
                                            icon={isCopy ? faCheck : faClipboard}
                                            className={`copy-icon ${isCopy ? 'copied' : ''}`}
                                        />
                                        <span className="link-text">{link}</span>
                                    </div>
                                </div>
                            </CopyToClipboard>
                        )}
                    </div>

                    {/* Instructions Section */}
                    <div className="instructions-section">
                        <h5>How to Connect Your Typeform:</h5>
                        <ol className="steps-list">
                            <li>Go to <a href="https://www.typeform.com" target="_blank" rel="noopener noreferrer">typeform.com</a> and open the form you want to connect</li>
                            <li>In the form editor, click <strong>Connect</strong> (top menu)</li>
                            <li>From the Connect page, select <strong>Webhooks</strong></li>
                            <li>Click <strong>Add a webhook</strong></li>
                            <li>Copy the <strong>Webhook URL</strong> above and paste it into Typeform</li>
                            <li>Click <strong>Save webhook</strong></li>
                            <li>Submit a test response to trigger the workflow 🚀</li>
                        </ol>
                        <a
                            href="https://www.typeform.com/help/a/webhooks-360029573471/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="external-link"
                        >
                            Typeform Webhooks Documentation
                            <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                        </a>
                    </div>
                </Styled>}
            </ExpandedBlock> : null}
        </Main>
    </>);
}